import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDate, fmtDateTime } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { createCase } from '../state/createCase';
import { acceptOnboarding, hasExecutedAgreement } from '../state/actions/onboarding';
import { CaseStageCell } from '../components/CaseProgressTracker';
import { isSentToVetting } from '../domain/case-progress';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { QueueColumnPicker, useQueueColumns } from '../components/QueueColumns';
import type { ReactNode } from 'react';
import type { Case } from '../domain/initial-cases';
import { notificationContactLabel } from '../state/actions/lifecycle';
import { formatOrgType } from '../domain/constants';

export function Onboarding() {
  const { cases, addCase, showToast, updateCase } = useAppData();
  const { openCase, setCaseTab, setModal, setModalData } = useUIState();
  const user = getCurrentUser()!;

  const drafts = cases.filter(c => c.onboarding?.status === 'DRAFT');
  const submitted = cases.filter(c => c.onboarding?.status === 'SUBMITTED');
  // #766 — A CASE SENT TO VETTING LEAVES THIS QUEUE. It used to stay listed as
  // "Accepted", so the handoff was visible only as a change of badge; now it
  // drops off here and turns up on the Cases list with a "New" badge until its
  // OA/VA Agent opens it. The "Sent to vetting" count below still says how many
  // went. `isSentToVetting` reads both marks a vetting entry leaves, so a case
  // that reached review by another door (a create straight into IN_REVIEW)
  // leaves too.
  const sent = cases.filter(isSentToVetting);
  const inOnboarding = cases.filter(c => !isSentToVetting(c));

  // Search + filter applies to the "All Onboarding Applications" table; the
  // Drafts / Awaiting-Review triage cards above stay full so nothing pending
  // gets hidden behind a query.
  const { filtered: filteredCases, bar } = useSearchFilter(
    inOnboarding,
    // #693 — the authorizer's name in place of the retired oaName. The API's
    // case search matches the same key (`onboarding->'data'->>'authorizerName'`
    // in routes/cases.ts), so a query typed here and a query sent there find
    // the same applications.
    c => [c.id, c.onboarding?.data?.legalName, c.registrant, c.onboarding?.data?.authorizerName, c.onboarding?.data?.fein, c.onboarding?.data?.jurisdiction].filter(Boolean).join(' '),
    [
      {
        id: 'onboardingStatus',
        label: 'Onboarding status',
        options: [
          { value: '', label: 'All onboarding statuses' },
          { value: 'DRAFT', label: 'Draft' },
          { value: 'SUBMITTED', label: 'Submitted' },
        ],
        predicate: (c, v) => (c.onboarding?.status ?? '') === v,
      },
    ],
    { placeholder: 'Search by case, registrant, authorizer, FEIN, or jurisdiction…' },
  );

  // #540 — this analyst's own column choice for the All Onboarding
  // Applications table, or the eight columns it has always shown.
  const columns = useQueueColumns('onboarding');

  const cell = (key: string, c: Case): ReactNode => {
    switch (key) {
      case 'case':
        return <span style={{ color: C.goldText, fontWeight: 600 }}>{c.id}</span>;
      case 'registrant':
        return c.onboarding?.data?.legalName || c.registrant || '—';
      // #540 — the CASE RCD, not `c.onboarding.data`. This queue is where the
      // RCD is actively worked, so it is the queue most likely to be looked at
      // right after somebody amended one; reading the submitted payload here
      // would show the analyst the value they just replaced.
      case 'displayName':
        return c.rcd?.displayName || <span style={{ color: C.textMuted }}>—</span>;
      case 'callReason':
        return c.rcd?.callReason || <span style={{ color: C.textMuted }}>—</span>;
      case 'oa':
        return <span style={{ fontSize: 12, color: C.textMuted }}>{notificationContactLabel(c)}</span>;
      case 'fein':
        return <span style={{ fontSize: 11, fontFamily: 'monospace' }}>{c.onboarding?.data?.fein || '—'}</span>;
      case 'jurisdiction':
        return c.onboarding?.data?.jurisdiction || '—';
      case 'onboardingStatus':
        return (
          // #766 — no "Accepted" branch: a READY_FOR_VETTING case is not in
          // this table any more (isSentToVetting above).
          <span style={{ ...styles.badge, background: c.onboarding?.status === 'SUBMITTED' ? C.warnBg : C.infoBg, color: c.onboarding?.status === 'SUBMITTED' ? C.warn : C.info }}>
            {c.onboarding?.status || 'N/A'}
          </span>
        );
      // #766 — the case progress tracker's stage and its first open item.
      case 'stage':
        return <CaseStageCell c={c} />;
      case 'submitted':
        return fmtDate(c.onboarding?.submittedAt);
      case 'vettingStatus':
        return <StatusBadge status={c.status} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Onboarding Applications</h2>
        <button style={{ ...styles.btn, ...styles.btnGold }} onClick={async () => {
          try {
            const newCase = await createCase({
              status: 'ONBOARDING',
              // #540 fold-in B — creating a case assigns it to its creator, and
              // BOTH doors do it: "+ New Case" on the Cases page already sent
              // this, so an application started here arrived unassigned and sat
              // on nobody's queue.
              //
              // `user.id` is the Keycloak `sub`, which is what the SPA holds and
              // what the API's resolvePrincipals() translates into the
              // server-generated users.id that cases.analyst_id stores (B-2, the
              // two-user-ID-spaces rule in CLAUDE.md). `user.serverId` is for
              // COMPARING principals and would be the wrong value to write.
              analystId: user.id,
              action: { code: 'ONBOARDING_STARTED', detail: { source: 'applications-list' } },
            });
            addCase(newCase);
            openCase(newCase);
            setCaseTab('onboarding');
          } catch (err) {
            showToast(`Failed to create application: ${err instanceof Error ? err.message : String(err)}`);
          }
        }}>+ New Application</button>
      </div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>Collect application data from registrants → submit → promote to vetting queue</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.info}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.info }}>{drafts.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Drafts</div>
        </div>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.warn}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.warn }}>{submitted.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Submitted — Awaiting Review</div>
        </div>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.success}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.success }}>{sent.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Sent to vetting</div>
        </div>
      </div>
      {submitted.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>⚠ Awaiting Review ({submitted.length})</h3>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>The registrant has submitted application data — review completeness and accept to start vetting SLA</div>
          {submitted.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong style={{ color: C.goldText }}>{c.id}</strong> — <strong>{c.onboarding?.data?.legalName || c.registrant || 'Unnamed'}</strong>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  Authorizer: {notificationContactLabel(c)} · Submitted: {fmtDateTime(c.onboarding?.submittedAt)} · FEIN: {c.onboarding?.data?.fein || '—'}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                  {formatOrgType(c.onboarding?.data?.orgType)} · {c.onboarding?.data?.jurisdiction} · {c.onboarding?.data?.website}
                </div>
                {/* #251: was a title= tooltip on the Send to vetting button —
                    hover-only, so keyboard and touch users never saw why it
                    would fail. */}
                {!hasExecutedAgreement(c) && (
                  <div style={{ fontSize: 11, color: C.warn, marginTop: 4 }}>
                    ⚠ Requires an executed Registrant Agreement — open Review Data to confirm it
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { openCase(c, 'onboarding'); setCaseTab('onboarding'); }}>Review Data</button>
                {user.canEdit && (
                  <button
                    style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }}
                    onClick={() => {
                      // D6: same full-validation path as the OnboardingTab's Send to vetting.
                      const result = acceptOnboarding(c, updateCase, 'applications-list');
                      if (!result.ok) { setModal('validation-error'); setModalData({ missing: ['agreement'], message: result.message }); }
                    }}
                  >Send to vetting</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {drafts.length > 0 && (
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Drafts ({drafts.length})</h3>
          {drafts.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong style={{ color: C.goldText }}>{c.id}</strong> — <strong>{c.onboarding?.data?.legalName || 'Unnamed application'}</strong>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Created: {fmtDate(c.createdAt)}</div>
              </div>
              <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => { openCase(c, 'onboarding'); setCaseTab('onboarding'); }}>Continue Editing</button>
            </div>
          ))}
        </div>
      )}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>All Onboarding Applications</h3>
          <QueueColumnPicker queue="onboarding" onError={showToast} />
        </div>
        <SearchFilterBar {...bar} />
        <table style={styles.table}>
          <thead><tr>
            {columns.map(col => <th key={col.key} style={styles.th}>{col.label}</th>)}
          </tr></thead>
          <tbody>
            {filteredCases.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { openCase(c, 'onboarding'); setCaseTab('onboarding'); }}>
                {columns.map(col => <td key={col.key} style={styles.td}>{cell(col.key, c)}</td>)}
              </tr>
            ))}
            {filteredCases.length === 0 && (
              <tr><td colSpan={columns.length} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>
                {inOnboarding.length === 0 ? 'No onboarding applications' : 'No applications match the current filters'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
